import { Hourglass, Warning } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from '@/components/ui/cx';
import { useWide } from '@/hooks/useMediaQuery';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';
import { connectRealtime } from '@/lib/realtime';
import { useDashboard, useMe, useProductionOrders } from '@/lib/queries';
import { toast } from '@/lib/toast';

import { Header } from './Header';
import { finishImpersonation, ImpersonationBanner } from './ImpersonationBanner';
import { allowedWhilePending, PendingApproval, PendingShell } from './PendingApproval';
import { Sidebar } from './Sidebar';

export function AppShell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const wide = useWide();
  const [expanded, setExpanded] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabParam = useRouterState({
    select: (s) => (s.location.search as { tab?: string } | undefined)?.tab,
  });
  // Firma čeka odobrenje: server vraća 403 za sve osim podešavanja organizacije i profila, pa se
  // upiti modula ne pokreću.
  const pending = me?.tenant.status === 'pending_approval';
  const expired =
    !!me?.tenant.expiresAt && Date.parse(me.tenant.expiresAt) <= Date.now() && !me.impersonation;

  const canDash = hasPermission(me, 'dashboard.read') && !pending && !expired;
  const { data: dash } = useDashboard(canDash);
  const canProd =
    hasPermission(me, 'production.read') &&
    !pending &&
    !expired &&
    me?.features.production !== 'off';
  const { data: orders } = useProductionOrders(canProd);

  // Realtime: SSE -> invalidacija upita, toast za nova obavještenja.
  const userId = me?.user.id;
  useEffect(() => {
    if (!userId || pending || expired) return;
    return connectRealtime({
      queryClient: qc,
      onNotification: (ev) => {
        if (ev.notification?.title) toast(ev.notification.title, 'info');
      },
    });
  }, [userId, pending, expired, qc]);

  useEffect(() => setExpanded(false), [pathname]);

  if (!me) return null;

  const collapsed = !wide && !expanded;
  const counts = {
    projects: dash?.kpis.activeProjects,
    measurements: dash?.kpis.awaitingApproval,
    production: orders?.length,
  };

  const logout = async () => {
    // Za vrijeme impersonacije odjava završava impersonaciju (operater se vraća na platformu).
    if (me.impersonation) {
      await finishImpersonation(me, () => qc.clear()).catch(() => undefined);
      return;
    }
    try {
      await api.POST('/auth/logout');
    } finally {
      qc.clear();
      void navigate({ to: '/prijava', search: { redirect: undefined, reason: 'logout' } });
    }
  };

  if (pending) {
    return (
      <>
        <ImpersonationBanner me={me} />
        <PendingShell me={me} onLogout={() => void logout()}>
          {allowedWhilePending(pathname, tabParam) ? <Outlet /> : <PendingApproval me={me} />}
        </PendingShell>
      </>
    );
  }

  return (
    <>
      <ImpersonationBanner me={me} />
      <div
        className={cx(
          'grid min-h-screen items-start',
          collapsed ? 'grid-cols-[72px_minmax(0,1fr)]' : 'grid-cols-[252px_minmax(0,1fr)]',
        )}
      >
        <a
          href="#main"
          className="sr-only z-[200] rounded-md bg-btn px-3 py-2 text-btn-fg focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          {t('panel:shell.skipToContent')}
        </a>
        <div
          className={cx(
            'no-print h-dvh',
            !wide && expanded
              ? 'fixed inset-y-0 left-0 z-50 w-[252px] shadow-card'
              : 'sticky top-0',
          )}
        >
          <Sidebar
            me={me}
            counts={counts}
            collapsed={collapsed}
            showToggle={!wide}
            onToggle={() => setExpanded((x) => !x)}
            onLogout={() => void logout()}
          />
        </div>
        {!wide && expanded ? (
          <div
            className="fixed inset-0 z-40 bg-[rgba(10,27,51,.35)]"
            onClick={() => setExpanded(false)}
            aria-hidden
          />
        ) : null}
        <main id="main" className="flex min-h-screen min-w-0 flex-col" tabIndex={-1}>
          <Header onOpenMenu={() => setExpanded(true)} showMenuButton={false} canSettings />
          <div className="flex w-full max-w-[1480px] flex-col gap-6 px-7 pt-7 pb-16">
            {me.tenant.deletionScheduledAt ? (
              <div
                role="alert"
                className="flex items-center gap-2.5 rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm font-medium text-danger"
                data-testid="company-deletion-banner"
              >
                <Warning size={18} weight="fill" className="flex-none" />
                <span>
                  {t('panel:shell.companyDeletion', {
                    date: fmtDate(me.tenant.deletionScheduledAt),
                  })}
                </span>
              </div>
            ) : null}
            {me.tenant.status === 'trial' && me.tenant.trialEndsAt ? (
              <div
                className="flex items-center gap-2 self-start rounded-full bg-info-bg px-3 py-1.5 text-[13px] font-medium text-info"
                data-testid="trial-banner"
              >
                <Hourglass size={15} weight="bold" className="flex-none" />
                {t('panel:trial.banner', { date: fmtDate(me.tenant.trialEndsAt) })}
              </div>
            ) : null}
            {me.tenant.expiresAt ? (
              <div
                data-testid="subscription-expiration-banner"
                className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-4 py-3 text-sm"
              >
                <Hourglass size={18} />
                <span>
                  {expired
                    ? t('errors:subscription_expired')
                    : t('panel:platform.subscription.validUntil', {
                        date: fmtDate(me.tenant.expiresAt.slice(0, 10)),
                      })}
                </span>
                {expired ? (
                  <button
                    className="font-semibold text-accent underline"
                    onClick={() => void navigate({ to: '/podesavanja', search: { tab: 'paket' } })}
                  >
                    {t('panel:platform.subscription.renew')}
                  </button>
                ) : null}
              </div>
            ) : null}
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
